import React, { FC, useState, useRef, RefObject } from "react";
import classNames from "classnames";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronSmall } from "@app/components/common/icons";
import { useIsLGMediaQuery, useIsXXLMediaQuery } from "@app/hooks";
import { useControlButtons } from "./useControlButtons";
import { useEqualHeight } from "./useEqualHeight";
import { ControlButton } from "./components/control-button/ControlButton";
import { Card } from "./components/card/Card";
import { CarouselCard } from "./components/card/types";
import { cardSizes } from "./constants";
import { getCardsOverflow } from "./helpers";

type Props = {
  cards: CarouselCard[];
};

export const Carousel: FC<Props> = ({ cards }) => {
  const carouselContainerRef = useRef(null);
  const carouselContainerWidth =
    carouselContainerRef?.current?.offsetWidth ?? 0;
  const isXXLScreen = useIsXXLMediaQuery();
  const isLGScreen = useIsLGMediaQuery();
  const cardParameters = isXXLScreen
    ? cardSizes.xxl
    : isLGScreen
      ? cardSizes.lg
      : cardSizes.sm;
  const isCardsOverflow = getCardsOverflow(
    cards.length,
    cardParameters,
    carouselContainerWidth,
  );
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    active: isCardsOverflow,
  });
  const { onPrevButtonClick, onNextButtonClick } = useControlButtons(emblaApi);
  const [cardHeaderRefs] = useState<RefObject<HTMLDivElement>[]>(
    cards.map(() => useRef<HTMLDivElement>(null)),
  );
  const [cardParagraphRefs] = useState<RefObject<HTMLDivElement>[]>(
    cards.map(() => useRef<HTMLDivElement>(null)),
  );

  useEqualHeight(cardHeaderRefs);
  useEqualHeight(cardParagraphRefs);

  return (
    <>
      <div className={classNames("overflow-hidden")}>
        <div className={classNames("mx-5 lg:mx-16 xl:mx-24")} ref={emblaRef}>
          <div
            className={classNames(
              "flex max-w-container touch-pan-y overflow-visible text-primary-content",
              "xxl:mx-auto",
            )}
            ref={carouselContainerRef}
          >
            {cards.map((card, index) => (
              <Card
                key={card._uid}
                headerRef={cardHeaderRefs[index]}
                paragraphRef={cardParagraphRefs[index]}
                {...card}
              />
            ))}
          </div>
        </div>
      </div>
      {isCardsOverflow ? (
        <div
          className={classNames(
            "hidden",
            "lg:mx-auto lg:mt-5 lg:flex lg:justify-end lg:gap-x-6 lg:px-16",
            "xl:px-24",
            "xxl:mt-8 xxl:max-w-container-wide xxl:gap-x-7.5",
          )}
        >
          <ControlButton onClick={onPrevButtonClick}>
            <ChevronSmall className="h-3 w-3 stroke-primary-content" />
          </ControlButton>
          <ControlButton onClick={onNextButtonClick}>
            <ChevronSmall className="h-3 w-3 rotate-180 stroke-primary-content" />
          </ControlButton>
        </div>
      ) : null}
    </>
  );
};
